import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { BOOKMAKERS_LIST, isExcludedFromBestBook } from '../utils/constants';
import { formatGameTime, getPlayerHeadshotUrl } from '../utils/formatters';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useBooks } from '../contexts/BooksContext';
import PlayerOddsTable from './PlayerOddsTable';
import PlayerOddsChart from './PlayerOddsChart';
import PlayerStatsChart from './PlayerStatsChart';
import '../styles/BestBets.css';

const BestBetsPanel = ({ bestBets, onClose, getTeamLogoUrl, selectedSport, onPlayerClick, halfWidth, rightOffset, compact, onOddsClick, onGameClick, onGamePanelClick, onTrendsClick, topOffset }) => {
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [selectedPoint, setSelectedPoint] = useState('all');
  const [selectedBook, setSelectedBook] = useState('all');
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedConference, setSelectedConference] = useState('all');
  const [minOdds, setMinOdds] = useState(null);
  const [maxOdds, setMaxOdds] = useState(null);
  const [minOddsInput, setMinOddsInput] = useState('');
  const [maxOddsInput, setMaxOddsInput] = useState('');
  const [enabledTiers, setEnabledTiers] = useState({
    'edge-one': true,
    'edge-two': true,
    'edge-three': true
  });
  const [hideBet365, setHideBet365] = useState(false);
  const [showBet365Tooltip, setShowBet365Tooltip] = useState(false);
  const [selectedBetIndex, setSelectedBetIndex] = useState(null);
  const [panelView, setPanelView] = useState('table'); // 'table' | 'chart'
  const [chartParams, setChartParams] = useState(null); // { marketKey, point, isOver }
  const [activeMarketKey, setActiveMarketKey] = useState(null); // tracks selected market tab in odds table
  const { isDarkMode } = useDarkMode();
  const { bookOrder, bestBetsMaxDesktop, isBookEnabled } = useBooks();
  const hasInitialized = useRef(false);
  const minOddsDebounceRef = useRef(null);
  const maxOddsDebounceRef = useRef(null);
  const oddsOverlayRef = useRef(null);

  const marketTypes = [
    { value: 'all', label: 'All Markets' },
    { value: 'moneyline', label: 'Moneyline' },
    { value: 'spreads', label: 'Spreads' },
    { value: 'totals', label: 'Totals' },
    { value: 'halves', label: '1st Half' },
    { value: 'quarters', label: '1st Qtr' }
  ];

  const isMainlineBestBets = bestBets.some(bet => {
    const market = bet.market?.toLowerCase() || '';
    return market === 'moneyline' || market === 'h2h' ||
           market === 'spread' || market === 'spreads' || market === 'alt spread' ||
           market === 'totals' || market === 'total' || market === 'alt total' ||
           market === '1st half ml' || market === '1st qtr ml';
  });

  const betMatchesMarketType = (bet, marketType) => {
    if (marketType === 'all') return true;
    const market = bet.market?.toLowerCase() || '';
    if (marketType === 'moneyline') {
      return market === 'moneyline' || market === 'h2h';
    }
    if (marketType === 'spreads') {
      return market === 'spread' || market === 'spreads' || market === 'alt spread';
    }
    if (marketType === 'totals') {
      return market === 'totals' || market === 'total' || market === 'alt total';
    }
    if (marketType === 'halves') {
      return market === '1st half ml' || market.includes('half');
    }
    if (marketType === 'quarters') {
      return market === '1st qtr ml' || market.includes('qtr') || market.includes('quarter');
    }
    return true;
  };

  const parseOdds = (oddsStr) => {
    if (!oddsStr) return null;
    return parseInt(oddsStr.replace('+', ''), 10);
  };

  const oddsValues = bestBets
    .filter(bet => betMatchesMarketType(bet, selectedMarket))
    .map(bet => parseOdds(bet.odds))
    .filter(odds => odds !== null && !isNaN(odds));
  const oddsMin = oddsValues.length > 0 ? Math.min(...oddsValues) : -200;
  const oddsMax = oddsValues.length > 0 ? Math.max(...oddsValues) : 300;

  useEffect(() => {
    setMinOddsInput(minOdds !== null ? String(minOdds) : '');
  }, [minOdds]);

  useEffect(() => {
    setMaxOddsInput(maxOdds !== null ? String(maxOdds) : '');
  }, [maxOdds]);

  const handleMinOddsInputChange = (value) => {
    setMinOddsInput(value);
    if (minOddsDebounceRef.current) {
      clearTimeout(minOddsDebounceRef.current);
    }
    minOddsDebounceRef.current = setTimeout(() => {
      const val = parseInt(value, 10);
      if (!isNaN(val)) {
        setMinOdds(val);
      }
    }, 500);
  };

  const handleMaxOddsInputChange = (value) => {
    setMaxOddsInput(value);
    if (maxOddsDebounceRef.current) {
      clearTimeout(maxOddsDebounceRef.current);
    }
    maxOddsDebounceRef.current = setTimeout(() => {
      const val = parseInt(value, 10);
      if (!isNaN(val)) {
        setMaxOdds(val);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (minOddsDebounceRef.current) clearTimeout(minOddsDebounceRef.current);
      if (maxOddsDebounceRef.current) clearTimeout(maxOddsDebounceRef.current);
    };
  }, []);

  // Reset odds range when sport changes so it re-initializes with new data
  useEffect(() => {
    hasInitialized.current = false;
    setMinOdds(null);
    setMaxOdds(null);
  }, [selectedSport]);

  // Initialize odds range on first mount with data
  useEffect(() => {
    if (bestBets.length > 0 && !hasInitialized.current) {
      setMinOdds(oddsMin);
      setMaxOdds(oddsMax);
      hasInitialized.current = true;
    }
  }, [bestBets.length, oddsMin, oddsMax]);

  useEffect(() => {
    setSelectedPoint('all');
    setMinOdds(oddsMin);
    setMaxOdds(oddsMax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarket]);

  const uniquePoints = [...new Set(
    bestBets
      .filter(bet => bet.point !== null && bet.point !== undefined)
      .filter(bet => betMatchesMarketType(bet, selectedMarket))
      .map(bet => bet.point)
  )].sort((a, b) => a - b);

  // Book counts include both the primary best book and any Secondary books
  // surfaced via secondaryMatches, so Kalshi etc. show up as filter options
  // when they have an active edge on at least one row.
  const bookCounts = {};
  bestBets.forEach(bet => {
    if (bet.book) {
      const book = bet.book.toLowerCase();
      bookCounts[book] = (bookCounts[book] || 0) + 1;
    }
    if (Array.isArray(bet.secondaryMatches)) {
      bet.secondaryMatches.forEach(s => {
        const sBook = String(s.book).toLowerCase();
        // Only include visible Secondary books (skip hidden ones).
        if (!isBookEnabled(sBook)) return;
        bookCounts[sBook] = (bookCounts[sBook] || 0) + 1;
      });
    }
  });
  const uniqueBooks = Object.keys(bookCounts).sort();

  const uniqueGames = [...new Set(
    bestBets
      .filter(bet => bet.game)
      .map(bet => bet.game)
  )].sort();

  const uniqueConferences = [...new Set(
    bestBets
      .flatMap(bet => [bet.homeConference, bet.awayConference])
      .filter(conf => conf)
  )].sort();

  const betInvolvesTop25 = (bet) => {
    return (bet.homeRank && bet.homeRank <= 25) || (bet.awayRank && bet.awayRank <= 25);
  };

  const betMatchesConference = (bet, conference) => {
    if (conference === 'all') return true;
    if (conference === 'top25') return betInvolvesTop25(bet);
    return bet.homeConference === conference || bet.awayConference === conference;
  };

  const toggleTier = (tier) => {
    setEnabledTiers(prev => ({ ...prev, [tier]: !prev[tier] }));
  };

  const tierFilterActive = !enabledTiers['edge-one'] || !enabledTiers['edge-two'] || !enabledTiers['edge-three'];

  const filteredBets = bestBets.filter(bet => {
    const matchesMarket = betMatchesMarketType(bet, selectedMarket);
    const matchesConference = betMatchesConference(bet, selectedConference);
    const matchesPoint = selectedPoint === 'all' || bet.point === parseFloat(selectedPoint);
    const matchesBook = selectedBook === 'all'
      || bet.book.toLowerCase() === selectedBook
      || (bet.secondaryMatches && bet.secondaryMatches.some(s => String(s.book).toLowerCase() === selectedBook));
    const matchesGame = selectedGame === 'all' || bet.game === selectedGame;
    const betOdds = parseOdds(bet.odds);
    const matchesMinOdds = minOdds === null || betOdds === null || betOdds >= minOdds;
    const matchesMaxOdds = maxOdds === null || betOdds === null || betOdds <= maxOdds;
    const matchesTier = bet.tier ? enabledTiers[bet.tier] : true;
    const matchesBet365Filter = !hideBet365 || bet.book.toLowerCase() !== 'bet365';
    return matchesMarket && matchesConference && matchesPoint && matchesBook && matchesGame && matchesMinOdds && matchesMaxOdds && matchesTier && matchesBet365Filter;
  });

  const oddsFilterActive = minOdds !== null && maxOdds !== null && (minOdds !== oddsMin || maxOdds !== oddsMax);
  const isFiltered = selectedMarket !== 'all' || selectedPoint !== 'all' || selectedBook !== 'all' || selectedGame !== 'all' || selectedConference !== 'all' || oddsFilterActive || tierFilterActive || hideBet365;

  const displayedBets = bestBetsMaxDesktop !== null
    ? filteredBets.slice(0, bestBetsMaxDesktop)
    : filteredBets;
  const isLimited = bestBetsMaxDesktop !== null && filteredBets.length > bestBetsMaxDesktop;

  return (
    <div className="dark-panel" style={{
      position: 'fixed',
      top: `${topOffset || 130}px`,
      left: '5px',
      right: rightOffset || '5px',
      bottom: 0,
      backgroundColor: '#212529',
      zIndex: 999,
      display: 'flex',
      flexDirection: 'column',
      padding: '10px 0',
      borderRadius: '12px',
      overflow: 'hidden',
      transition: 'right 0.2s ease'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #333', flexShrink: 0 }}>
        <span style={{ color: '#fff', fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: '600' }}>BEST BETS ({filteredBets.length})</span>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '20px',
            cursor: 'pointer',
            lineHeight: 1,
            padding: '0 4px'
          }}
        >
          ×
        </button>
      </div>

      {bestBets.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted">No best bets available right now.</p>
          <small>Best bets are highlighted when one book has significantly better odds.</small>
        </div>
      ) : (
        <div className={`best-bets-dual-table-container${compact ? ' best-bets-compact' : ''}${halfWidth ? ' best-bets-half' : ''}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Filter Row */}
          <div className="best-bets-filter-row">
            {isMainlineBestBets && (
              <div className="best-bets-filter-group">
                <label htmlFor="panel-market-filter">Market:</label>
                <select
                  id="panel-market-filter"
                  value={selectedMarket}
                  onChange={(e) => setSelectedMarket(e.target.value)}
                  className="best-bets-filter-select"
                >
                  {marketTypes.map(mt => (
                    <option key={mt.value} value={mt.value}>{mt.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="best-bets-filter-group">
              <label htmlFor="panel-point-filter">Line:</label>
              <select
                id="panel-point-filter"
                value={selectedPoint}
                onChange={(e) => setSelectedPoint(e.target.value)}
                className="best-bets-filter-select"
              >
                <option value="all">All Lines</option>
                {uniquePoints.map(point => (
                  <option key={point} value={point}>{point}</option>
                ))}
              </select>
            </div>
            <div className="best-bets-filter-group">
              <label htmlFor="panel-book-filter">Book:</label>
              <select
                id="panel-book-filter"
                value={selectedBook}
                onChange={(e) => setSelectedBook(e.target.value)}
                className="best-bets-filter-select"
              >
                <option value="all">All Books ({bestBets.filter(bet => bet.book).length})</option>
                {uniqueBooks.map(book => (
                  <option key={book} value={book}>
                    {book.charAt(0).toUpperCase() + book.slice(1)} ({bookCounts[book]})
                  </option>
                ))}
              </select>
            </div>
            {(selectedSport === 'ncaab' || selectedSport === 'ncaaf') && uniqueConferences.length > 0 && (
              <div className="best-bets-filter-group">
                <label htmlFor="panel-conference-filter">Conference:</label>
                <select
                  id="panel-conference-filter"
                  value={selectedConference}
                  onChange={(e) => setSelectedConference(e.target.value)}
                  className="best-bets-filter-select"
                >
                  <option value="all">All</option>
                  <option value="top25">Top 25</option>
                  {uniqueConferences.map(conf => (
                    <option key={conf} value={conf}>{conf}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="best-bets-filter-group">
              <label htmlFor="panel-game-filter">Game:</label>
              <select
                id="panel-game-filter"
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
                className="best-bets-filter-select"
              >
                <option value="all">All Games</option>
                {uniqueGames.map(game => (
                  <option key={game} value={game}>{game}</option>
                ))}
              </select>
            </div>
            {/* Tier Filter Icons */}
            <div className="best-bets-filter-group" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ marginRight: '4px' }}>Tiers:</label>
              <div
                onClick={() => toggleTier('edge-one')}
                title="Green tier (≥25% edge)"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: enabledTiers['edge-one'] ? 'green' : '#333',
                  border: enabledTiers['edge-one'] ? '2px solid #4ade80' : '2px solid #555',
                  cursor: 'pointer',
                  opacity: enabledTiers['edge-one'] ? 1 : 0.4,
                  transition: 'all 0.2s ease'
                }}
              />
              <div
                onClick={() => toggleTier('edge-two')}
                title="Orange tier (≥15% edge)"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: enabledTiers['edge-two'] ? '#F59E0B' : '#333',
                  border: enabledTiers['edge-two'] ? '2px solid #fbbf24' : '2px solid #555',
                  cursor: 'pointer',
                  opacity: enabledTiers['edge-two'] ? 1 : 0.4,
                  transition: 'all 0.2s ease'
                }}
              />
              <div
                onClick={() => toggleTier('edge-three')}
                title="Blue tier (≥5% edge)"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: enabledTiers['edge-three'] ? '#3B82F6' : '#333',
                  border: enabledTiers['edge-three'] ? '2px solid #60a5fa' : '2px solid #555',
                  cursor: 'pointer',
                  opacity: enabledTiers['edge-three'] ? 1 : 0.4,
                  transition: 'all 0.2s ease'
                }}
              />
            </div>
            <div className="best-bets-odds-row">
              <div className="best-bets-filter-group best-bets-odds-filter">
                <label>Min Odds:</label>
                <input
                  type="range"
                  min={oddsMin}
                  max={oddsMax}
                  value={minOdds ?? oddsMin}
                  onChange={(e) => setMinOdds(parseInt(e.target.value, 10))}
                  className="best-bets-slider"
                />
                <input
                  type="number"
                  className="best-bets-odds-input"
                  value={minOddsInput || (minOdds ?? oddsMin)}
                  onChange={(e) => handleMinOddsInputChange(e.target.value)}
                />
              </div>
              <div className="best-bets-filter-group best-bets-odds-filter">
                <label>Max Odds:</label>
                <input
                  type="range"
                  min={oddsMin}
                  max={oddsMax}
                  value={maxOdds ?? oddsMax}
                  onChange={(e) => setMaxOdds(parseInt(e.target.value, 10))}
                  className="best-bets-slider"
                />
                <input
                  type="number"
                  className="best-bets-odds-input"
                  value={maxOddsInput || (maxOdds ?? oddsMax)}
                  onChange={(e) => handleMaxOddsInputChange(e.target.value)}
                />
              </div>
            </div>
            {/* Hide Bet365 toggle - NCAAB only */}
            {selectedSport === 'ncaab' && (
              <div className="best-bets-filter-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={hideBet365}
                    onChange={(e) => setHideBet365(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span>Hide Bet365</span>
                </label>
                <div
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => setShowBet365Tooltip(!showBet365Tooltip)}
                  onMouseEnter={() => setShowBet365Tooltip(true)}
                  onMouseLeave={() => setShowBet365Tooltip(false)}
                >
                  ?
                </div>
                {showBet365Tooltip && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: '8px',
                    padding: '10px 12px',
                    backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    width: '280px',
                    fontSize: '13px',
                    color: isDarkMode ? '#e5e7eb' : '#374151',
                    lineHeight: '1.4'
                  }}>
                    NCAAB alt lines for Bet365 have been inconsistent. To exclude them from Best Bets, click the toggle.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fixed Header Table */}
          <div className="best-bets-header-container">
            <Table bordered size="sm" className="best-bets-header-table">
              <thead>
                <tr>
                  <th className="best-bets-game-cell">Game</th>
                  <th className="best-bets-player-cell">Player</th>
                  <th className="best-bets-market-cell">Market</th>
                  {halfWidth ? (
                    <>
                      <th className="best-bets-odds-cell">Book</th>
                      <th className="best-bets-odds-cell">Odds</th>
                    </>
                  ) : (
                    bookOrder.map(book => (
                      <th key={book} className="best-bets-odds-cell">
                        <img
                          src={`/logos/${book}.png`}
                          alt={book}
                          style={{ height: '16px' }}
                          title={book.charAt(0).toUpperCase() + book.slice(1)}
                        />
                      </th>
                    ))
                  )}
                  <th className="best-bets-advantage-cell">Adv</th>
                </tr>
              </thead>
            </Table>
          </div>

          {/* Scrollable Body Table */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <div className="best-bets-body-container" style={{ height: '100%', overflow: 'auto' }}>
              <Table bordered size="sm" className="best-bets-body-table">
                <tbody>
                  {displayedBets.map((bet, index) => {
                    const bestBookKey = bet.book.toLowerCase();

                    return (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? 'best-bets-even-row' : 'best-bets-odd-row'}
                        style={bet.secondaryMatches && bet.secondaryMatches.length > 0 ? {
                          backgroundImage: 'linear-gradient(225deg, #9CA3AF 8px, transparent 8px)',
                          backgroundRepeat: 'no-repeat',
                        } : undefined}
                        title={bet.secondaryMatches && bet.secondaryMatches.length > 0
                          ? `Also at ${bet.secondaryMatches.map(s => `${s.book} ${s.price > 0 ? '+' : ''}${s.price}`).join(', ')}`
                          : undefined}
                      >
                        <td className="best-bets-game-cell"
                          style={{ textAlign: 'center', cursor: (onGameClick || onTrendsClick) ? 'pointer' : undefined }}
                          onClick={bet.gameId ? () => {
                            if (bet.playerId && onTrendsClick) {
                              onTrendsClick(bet.gameId);
                            } else if (onGameClick) {
                              onGameClick(bet.gameId);
                            }
                          } : undefined}
                        >
                          <div>
                            <div>{bet.game}</div>
                            {bet.gameTime && (
                              <small className="text-muted d-block" style={{ marginTop: '2px', fontSize: '0.75rem' }}>
                                {formatGameTime(bet.gameTime)}
                              </small>
                            )}
                          </div>
                        </td>
                        <td className="best-bets-player-cell"
                          onClick={() => {
                            const bet = displayedBets[index];
                            if (selectedSport === 'ncaab' && onOddsClick && bet.gameId && bet.playerId) {
                              const bestBookKey = bet.book.toLowerCase();
                              onOddsClick(bet.gameId, bet.playerId, bet.player, bet.marketKey, bestBookKey, bet.odds, bet.awayTeam, bet.homeTeam, bet.team, null, bet.point, bet.side || (bet.betType || '').toLowerCase(), bet.teamId);
                            } else if (onPlayerClick && bet.gameId && bet.playerId) {
                              onPlayerClick(bet.gameId, bet.playerId, bet.player, bet.marketKey, bet.awayTeam, bet.homeTeam, bet.team, null, null);
                            } else if (onGamePanelClick && !bet.playerId && bet.gameId) {
                              onGamePanelClick(bet.gameId);
                            } else if (selectedBetIndex === index) {
                              setSelectedBetIndex(null);
                            } else {
                              setSelectedBetIndex(index);
                              setPanelView('table');
                              setChartParams(null);
                              setActiveMarketKey(null);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: halfWidth ? '3px' : '6px' }}>
                          {bet.team && (
                            <img
                              src={bet.teamLogo || getTeamLogoUrl(bet.team)}
                              alt={bet.team}
                              className="best-bets-team-logo"
                              style={{ flexShrink: 0 }}
                            />
                          )}
                          {bet.market === 'Totals' && (bet.betType === 'Over' || bet.betType === 'Under') && (
                            <div style={{
                              flexShrink: 0,
                              width: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              color: bet.betType === 'Over' ? '#10B981' : '#EF4444'
                            }}>
                              {bet.betType === 'Over' ? '↑' : '↓'}
                            </div>
                          )}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '0.85rem', lineHeight: '1.2' }}>
                              {bet.player}
                              {bet.battingOrder && (
                                <sup style={{
                                  fontSize: '0.6em',
                                  color: '#B3B3B3',
                                  fontWeight: 'normal',
                                  lineHeight: '1',
                                  marginLeft: '2px'
                                }}>
                                  {bet.battingOrder}
                                </sup>
                              )}
                              {bet.position && (
                                <span style={{
                                  fontSize: '0.75rem',
                                  color: '#9CA3AF',
                                  fontWeight: 'normal',
                                  marginLeft: '4px'
                                }}>
                                  ({bet.position})
                                </span>
                              )}
                            </div>
                            {bet.awayPitcher && bet.homePitcher && bet.awayPitcher !== 'TBD' && bet.homePitcher !== 'TBD' && (
                              <div style={{
                                fontSize: '0.65rem',
                                color: '#9CA3AF',
                                lineHeight: '1.1'
                              }}>
                                {bet.awayPitcher.split(' ').pop()} vs {bet.homePitcher.split(' ').pop()}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="best-bets-market-cell">
                        <div>
                          <div>{bet.market === 'Doubles' ? '2B' : bet.market === 'Home Runs' ? 'HR' : bet.market === 'Hits' ? 'H' : bet.market === 'RBIs' ? 'RBI' : bet.market === 'Runs' ? 'R' : bet.market === 'Hits + Runs + RBIs' ? 'H+R+R' : bet.market === 'Total Bases' ? 'TB' : bet.market === 'Stolen Bases' ? 'SB' : bet.market === 'Strikeouts' ? 'K' : bet.market === 'Total Touchdowns' ? 'TDs' : bet.market === 'Rush Attempts' ? 'Rush Att' : bet.market === 'Reception Yards' ? 'Rec Yards' : bet.market === 'First Goal Scorer' ? '1st Goal' : bet.market === 'Last Goal Scorer' ? 'Last Goal' : bet.market === 'Shots on Goal' ? 'Shots' : bet.market === 'First Team Basket' ? 'First Team' : bet.market === 'Points + Rebounds + Assists' ? 'P + R + A' : bet.market === 'Points + Rebounds' ? 'P + R' : bet.market === 'Points + Assists' ? 'P + A' : bet.market === 'Rebounds + Assists' ? 'R + A' : (bet.market || '').includes('Touchdown') ? bet.market.replace('Touchdown', 'TD') : bet.market}</div>
                          {bet.point !== null && bet.point !== undefined && (() => {
                            const bt = (bet.betType || '').toLowerCase();
                            const market = (bet.market || '').toLowerCase();
                            if (bet.point === 'yes' || bet.point === 'no') {
                              return <small className="text-muted d-block">{bet.point === 'yes' ? 'Yes' : 'No'}</small>;
                            }
                            if (bt === 'over' || bt === 'under') {
                              return <small className="text-muted d-block">{bt === 'over' ? 'Over' : 'Under'} {bet.point}</small>;
                            }
                            if (bt === 'underdog' || bt === 'favorite' || market.includes('spread')) {
                              const sign = bt === 'underdog' || bt === 'plus' ? '+' : '-';
                              return <small className="text-muted d-block">{sign}{Math.abs(bet.point)}</small>;
                            }
                            if (typeof bet.point === 'number') {
                              return <small className="text-muted d-block">{bet.point}</small>;
                            }
                            return null;
                          })()}
                        </div>
                      </td>
                      {halfWidth ? (
                        <>
                          <td className="best-bets-odds-cell" style={{ textAlign: 'center' }}>
                            <img
                              src={`/logos/${bestBookKey}.png`}
                              alt={bet.book}
                              style={{ height: '25px', width: '25px', objectFit: 'contain' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </td>
                          <td
                            className="best-bets-odds-cell"
                            style={onOddsClick ? { cursor: 'pointer' } : undefined}
                            onClick={onOddsClick ? () => onOddsClick(bet.gameId, bet.playerId, bet.player, bet.marketKey, bestBookKey, bet.odds, bet.awayTeam, bet.homeTeam, bet.team, null, bet.point, bet.side || (bet.betType || '').toLowerCase(), bet.teamId) : undefined}
                          >
                            <span className={`edge-pill-${bet.tier === 'edge-three' && isDarkMode ? 'three-dark' : bet.tier.replace('edge-', '')}`}>
                              {String(bet.odds).startsWith('+') || String(bet.odds).startsWith('-') ? bet.odds : `+${bet.odds}`}
                            </span>
                          </td>
                        </>
                      ) : (() => {
                        const isBet365Best = bestBookKey === 'bet365' && selectedSport === 'ncaab';
                        let nextBestBook = null;
                        let nextBestTier = null;

                        if (isBet365Best) {
                          const allBooks = Object.keys(bet.allOdds || {}).filter(key => !key.includes('_isAvailable'));
                          const otherOdds = allBooks
                            .filter(book => book !== 'bet365' && !isExcludedFromBestBook(book) && bet.allOdds[book] !== null && bet.allOdds[book] !== undefined)
                            .map(book => ({ book, odds: bet.allOdds[book] }))
                            .sort((a, b) => b.odds - a.odds);

                          if (otherOdds.length >= 1) {
                            nextBestBook = otherOdds[0].book;

                            if (otherOdds.length >= 2) {
                              const nextBestOdds = otherOdds[0].odds;
                              const thirdBestOdds = otherOdds[1].odds;

                              let diff;
                              if ((nextBestOdds > 0 && thirdBestOdds > 0) || (nextBestOdds < 0 && thirdBestOdds < 0)) {
                                diff = Math.abs((nextBestOdds - thirdBestOdds) / thirdBestOdds);
                              } else if (nextBestOdds > 0 && thirdBestOdds < 0) {
                                const nextDistance = nextBestOdds - 100;
                                const thirdDistance = thirdBestOdds + 100;
                                diff = Math.abs((nextDistance - thirdDistance) / thirdBestOdds);
                              } else {
                                const nextDistance = nextBestOdds + 100;
                                const thirdDistance = thirdBestOdds - 100;
                                diff = Math.abs((nextDistance - thirdDistance) / thirdBestOdds);
                              }

                              if (diff >= 0.25) {
                                nextBestTier = 'edge-one';
                              } else if (diff >= 0.15) {
                                nextBestTier = 'edge-two';
                              } else if (diff >= 0.05) {
                                nextBestTier = 'edge-three';
                              }
                            }
                          }
                        }

                        return bookOrder.map(book => {
                          const odds = bet.allOdds[book];
                          const hasOdds = odds !== null && odds !== undefined;
                          const isBestBook = bestBookKey === book;
                          const isNextBestWhenBet365 = isBet365Best && book === nextBestBook;
                          const isAvailable = bet.allOdds[`${book}_isAvailable`] !== false;

                          let pillClass = '';
                          if (isBestBook && hasOdds) {
                            if (bet.tier === 'edge-three' && isDarkMode) {
                              pillClass = 'edge-pill-three-dark';
                            } else {
                              pillClass = `edge-pill-${bet.tier.replace('edge-', '')}`;
                            }
                          } else if (isNextBestWhenBet365 && hasOdds && nextBestTier) {
                            if (nextBestTier === 'edge-three' && isDarkMode) {
                              pillClass = 'edge-pill-three-dark';
                            } else {
                              pillClass = `edge-pill-${nextBestTier.replace('edge-', '')}`;
                            }
                          }

                          // Corner caret on Secondary book cells that tie or beat the primary best.
                          const primaryOdds = bet.allOdds[bestBookKey];
                          const isSecondary = isExcludedFromBestBook(book);
                          const secondaryTiesOrBeats = isSecondary && hasOdds && primaryOdds != null && (
                            (odds > 0 && primaryOdds > 0 && odds >= primaryOdds) ||
                            (odds < 0 && primaryOdds < 0 && odds >= primaryOdds)
                          );
                          let secondaryTooltip;
                          let caretColor;
                          if (secondaryTiesOrBeats) {
                            if (odds === primaryOdds) {
                              secondaryTooltip = `${book} ${odds > 0 ? '+' : ''}${odds} — tied with best book`;
                              caretColor = '#9CA3AF'; // tie — neutral grey
                            } else {
                              const edge = Math.abs((odds - primaryOdds) / primaryOdds);
                              const edgePct = edge * 100;
                              secondaryTooltip = `${book} ${odds > 0 ? '+' : ''}${odds} — ${edgePct.toFixed(2)}% edge over best book`;
                              if (edge >= 0.25) caretColor = 'green';
                              else if (edge >= 0.15) caretColor = '#F59E0B';
                              else if (edge >= 0.05) caretColor = '#3B82F6';
                              else caretColor = '#9CA3AF';
                            }
                          }
                          const cellStyle = {
                            ...(!isAvailable ? { opacity: 0.25 } : hasOdds && onOddsClick ? { cursor: 'pointer' } : {}),
                            ...(secondaryTiesOrBeats ? {
                              backgroundImage: `linear-gradient(225deg, ${caretColor} 7px, transparent 7px)`,
                              backgroundRepeat: 'no-repeat',
                            } : {}),
                          };
                          return (
                            <td
                              key={book}
                              ref={index === 0 && book === bookOrder[0] ? oddsOverlayRef : undefined}
                              className={`best-bets-odds-cell ${!hasOdds ? 'unavailable' : ''}`}
                              style={Object.keys(cellStyle).length > 0 ? cellStyle : undefined}
                              title={secondaryTooltip}
                              onClick={hasOdds && onOddsClick ? () => onOddsClick(bet.gameId, bet.playerId, bet.player, bet.marketKey, book, odds, bet.awayTeam, bet.homeTeam, bet.team, null, bet.point, bet.side || (bet.betType || '').toLowerCase(), bet.teamId) : undefined}
                            >
                              {hasOdds ? (
                                pillClass ? <span className={pillClass}>{odds > 0 ? `+${odds}` : odds}</span> : (odds > 0 ? `+${odds}` : `${odds}`)
                              ) : ''}
                            </td>
                          );
                        });
                      })()}
                      <td className="best-bets-advantage-cell">
                        <Badge
                          className={`best-bets-badge tier-${bet.tier}`}
                        >
                          {(bet.advantage * 100).toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </Table>
            </div>

            {/* Overlay panel for selected bet detail */}
            {selectedBetIndex !== null && displayedBets[selectedBetIndex] && (() => {
              const bet = displayedBets[selectedBetIndex];
              const edgeBorderColor = bet.tier === 'edge-one' ? '#4ade80' : bet.tier === 'edge-two' ? '#fbbf24' : '#60a5fa';
              const bestBg = bet.tier === 'edge-one' ? 'green'
                : bet.tier === 'edge-two' ? '#F59E0B'
                : (isDarkMode ? '#3B82F6' : '#E5E7EB');
              const bestFg = bet.tier === 'edge-one' ? 'yellow'
                : bet.tier === 'edge-two' ? '#1E40AF'
                : (isDarkMode ? 'white' : '#374151');
              const overlayLeft = oddsOverlayRef.current ? oddsOverlayRef.current.offsetLeft : 0;
              console.log('[BestBetsPanel] Selected bet:', { gameId: bet.gameId, playerId: bet.playerId, market: bet.market, marketKey: bet.marketKey, player: bet.player, keys: Object.keys(bet) });
              const hasPlayerData = bet.gameId && bet.playerId;
              const sortedBooks = Object.entries(bet.allOdds || {})
                .filter(([key, val]) => !key.includes('_isAvailable') && val !== null && val !== undefined)
                .sort((a, b) => b[1] - a[1]);

              return (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: overlayLeft,
                    right: 0,
                    backgroundColor: 'rgba(20, 20, 20, 0.97)',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    padding: '16px 20px',
                    borderLeft: `2px solid ${edgeBorderColor}`,
                    overflow: 'auto'
                  }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) setSelectedBetIndex(null);
                  }}
                >
                  {/* Player header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexShrink: 0 }}>
                    {/* Player headshot + team logo combo */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      {hasPlayerData && getPlayerHeadshotUrl(bet.playerId, selectedSport) ? (
                        <img
                          src={getPlayerHeadshotUrl(bet.playerId, selectedSport)}
                          alt={bet.player}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            border: '2px solid #4b5563',
                            backgroundColor: '#1f2937',
                            objectFit: 'cover'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : bet.team ? (
                        <img
                          src={bet.teamLogo || getTeamLogoUrl(bet.team)}
                          alt={bet.team}
                          style={{ width: '40px', height: '40px' }}
                        />
                      ) : null}
                      {/* Team logo badge (overlaid on headshot) */}
                      {hasPlayerData && bet.team && getPlayerHeadshotUrl(bet.playerId, selectedSport) && (
                        <img
                          src={bet.teamLogo || getTeamLogoUrl(bet.team)}
                          alt={bet.team}
                          style={{
                            position: 'absolute',
                            bottom: '-2px',
                            right: '-6px',
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            backgroundColor: '#111',
                            border: '1px solid #333',
                            padding: '1px'
                          }}
                        />
                      )}
                    </div>
                    {bet.market === 'Totals' && (bet.betType === 'Over' || bet.betType === 'Under') && (
                      <div style={{
                        flexShrink: 0, width: '16px', height: '16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', color: bet.betType === 'Over' ? '#10B981' : '#EF4444'
                      }}>
                        {bet.betType === 'Over' ? '↑' : '↓'}
                      </div>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '0.95rem', lineHeight: '1.2', color: '#fff', fontWeight: 500 }}>
                        {bet.player}
                        {bet.battingOrder && (
                          <sup style={{ fontSize: '0.6em', color: '#B3B3B3', fontWeight: 'normal', lineHeight: '1', marginLeft: '2px' }}>
                            {bet.battingOrder}
                          </sup>
                        )}
                        {bet.position && (
                          <span style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 'normal', marginLeft: '4px' }}>
                            ({bet.position})
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', lineHeight: '1.3' }}>
                        {bet.game}
                      </div>
                      {bet.awayPitcher && bet.homePitcher && bet.awayPitcher !== 'TBD' && bet.homePitcher !== 'TBD' && (
                        <div style={{ fontSize: '0.65rem', color: '#9CA3AF', lineHeight: '1.1' }}>
                          {bet.awayPitcher.split(' ').pop()} vs {bet.homePitcher.split(' ').pop()}
                        </div>
                      )}
                    </div>
                    <span
                      onClick={(e) => { e.stopPropagation(); setSelectedBetIndex(null); }}
                      style={{ cursor: 'pointer', color: '#999', fontSize: '18px', lineHeight: '1', padding: '0 4px', flexShrink: 0 }}
                    >×</span>
                  </div>

                  {/* Content area */}
                  {hasPlayerData ? (
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
                      {panelView === 'chart' && chartParams ? (
                        <PlayerOddsChart
                          gameId={bet.gameId}
                          playerId={bet.playerId}
                          marketKey={chartParams.marketKey}
                          point={chartParams.point}
                          isOver={chartParams.isOver}
                          sport={selectedSport}
                          onBack={() => { setPanelView('table'); setChartParams(null); }}
                        />
                      ) : (
                        <>
                          <PlayerOddsTable
                            gameId={bet.gameId}
                            playerId={bet.playerId}
                            market={bet.marketKey || bet.market}
                            sport={selectedSport}
                            onChartClick={(marketKey, point, isOver) => {
                              setChartParams({ marketKey, point, isOver });
                              setPanelView('chart');
                            }}
                            onMarketChange={(marketKey) => setActiveMarketKey(marketKey)}
                          />
                          {(selectedSport === 'nba' || selectedSport === 'wnba') && (
                            <PlayerStatsChart
                              playerId={bet.playerId}
                              sport={selectedSport}
                              marketType={activeMarketKey || bet.marketKey}
                              playerTeam={bet.playerTeam}
                              awayTeam={bet.awayTeam}
                              homeTeam={bet.homeTeam}
                              lineValue={typeof bet.point === 'number' ? bet.point : null}
                            />
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    /* Fallback: sorted books list for mainline bets */
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      {sortedBooks.map(([book, odds], i) => {
                        const isAvailable = bet.allOdds[`${book}_isAvailable`] !== false;
                        const isBest = i === 0;
                        return (
                          <div key={book} style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            opacity: isAvailable ? 1 : 0.3,
                            ...(isBest ? {
                              backgroundColor: bestBg,
                              borderRadius: '4px',
                              padding: '2px 6px'
                            } : {})
                          }}>
                            <img src={`/logos/${book}.png`} alt={book} style={{ height: '20px' }} />
                            <span style={{
                              color: isBest ? bestFg : '#aaa',
                              fontSize: '0.9rem',
                              fontWeight: isBest ? 700 : 400
                            }}>
                              {odds > 0 ? `+${odds}` : odds}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Footer count */}
          <div style={{ color: '#94a3b8', fontSize: '12px', padding: '8px 0 0', flexShrink: 0 }}>
            Showing {displayedBets.length}{isLimited ? ` (max ${bestBetsMaxDesktop})` : ''} of {filteredBets.length} filtered / {bestBets.length} total
          </div>
        </div>
      )}
    </div>
  );
};

export default BestBetsPanel;
